'use client'

import { useMemo } from 'react'
import type { PolicyItem } from '@/app/settings/portal/_components/portal-preview'
import { useTranslation } from '@/lib/i18n'
import { useKnowledgeLibrary } from '@/lib/knowledge/portal-api'
import type { LibraryItem } from '@/lib/knowledge/portal-types'

/** How many documents the portal's Policies section shows (K1 contract). */
export const LATEST_POLICIES = 5

/**
 * The library as the portal's "Policies" cards in Important Updates: the five
 * most recently updated documents, newest first, each linking to itself in the
 * library and dated by when it was last updated.
 */
export function libraryToPolicyItems(items: LibraryItem[], t: (key: string) => string): PolicyItem[] {
  return [...items]
    .sort((a, b) => (Date.parse(b.updatedAt) || 0) - (Date.parse(a.updatedAt) || 0))
    .slice(0, LATEST_POLICIES)
    .map((item) => ({
      category: t(`knowledgePortal.kinds.${item.kind}`),
      title: item.title,
      description: item.description ?? '',
      image: '',
      href: `/portal/library?open=${encodeURIComponent(item.id)}`,
      publishedAt: item.updatedAt,
    }))
}

/** The Policies cards for the live portal; none (and no request) while the section's switch is off. */
export function useLatestPolicyItems(enabled: boolean): PolicyItem[] {
  const { t } = useTranslation()
  const { items } = useKnowledgeLibrary(null, enabled)
  return useMemo(() => (enabled ? libraryToPolicyItems(items, t) : []), [enabled, items, t])
}
